// dashboard.tsx : an observational operations home (no publish/deploy actions here).
// Layout: a top exchange-rate bar, then site traffic, then a service/tickets/your-changes row, then
// the team's recent changes + translation coverage. The greeting is the page-header title.
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import type { LogEntry } from "../API/Models";
import { usePageHeader } from "../hooks/usePageHeader";
import { useLogsData } from "../hooks/useLogsData";
import useNetworkStatusData from "../hooks/useNetworkStatusData";
import { useExchangeRates, useTranslationCoverage } from "../hooks/useDashboardExtras";
import { useRealtime, useRealtimeEvent } from "../Realtime/RealtimeProvider";
import { activityToLogEntry, mergeActivityEntry, type ActivityPayload } from "../Realtime/activityStream";
import { FxBar } from "../Components/dashboard/FxBar";
import { ServiceStatus } from "../Components/dashboard/ServiceStatus";
import { TicketsPanel } from "../Components/dashboard/TicketsPanel";
import { ChangesFeed } from "../Components/dashboard/ChangesFeed";
import { RecentChangesModal } from "../Components/dashboard/RecentChangesModal";
import { AnalyticsSection } from "../Components/dashboard/AnalyticsSection";
import { ArticleCards } from "../Components/dashboard/ArticleCards";
import { TranslationCoverage } from "../Components/dashboard/TranslationCoverage";
import { PreviewAccessCard } from "../Components/dashboard/PreviewAccessCard";
import { TeamCard } from "../Components/dashboard/TeamCard";
import { getMyProfile } from "../Components/Account/accountClient";

const LOG_LIMIT = 40;
const FEED_CAP = 80;

const Dashboard: React.FC = () => {
	const { user } = useAuth0();
	// Greeting can use the friendly Auth0 name, but the "me" change filter MUST use the SAME key the
	// server stamps as author : the local display name (Auth0 access tokens have no name claim, so the
	// two can differ). Fetch it once and fall back to the token name until it resolves.
	const greetingName = user?.given_name ?? user?.nickname ?? user?.name ?? user?.email ?? "Admin";
	const [profileName, setProfileName] = useState<string | null>(null);
	useEffect(() => {
		let alive = true;
		getMyProfile()
			.then((p) => { if (alive) setProfileName(p.displayName ?? null); })
			.catch(() => { /* keep the token-name fallback */ });
		return () => { alive = false; };
	}, []);
	const changeAuthorName = profileName ?? greetingName;
	// Email of the signed-in account: the STABLE identity key for "Vous" and the "me" filter.
	// Display names collide (two accounts can share a name); the email never does.
	const changeAuthorEmail = user?.email ?? undefined;

	// The greeting replaces the generic "Dashboard" page title in the top header (welcome block removed).
	usePageHeader({ title: `Bonjour, ${greetingName}` });

	// Presence count/avatars now live only in the top navbar (PresenceBar); the dashboard keeps just the
	// connection flag for the service-status/feed widgets.
	const { connected } = useRealtime();

	// Activity: polled then kept live by the "activity" SignalR channel. Feeds both change panels.
	const { logs: polledLogs, loading: logsLoading } = useLogsData({ limit: LOG_LIMIT, includeDebug: false });
	const [feed, setFeed] = useState<LogEntry[]>([]);
	useEffect(() => {
		setFeed(polledLogs);
	}, [polledLogs]);
	useRealtimeEvent("activity", (payload: ActivityPayload) => {
		setFeed((prev) => mergeActivityEntry(prev, activityToLogEntry(payload), FEED_CAP));
	});

	const { data: networkData } = useNetworkStatusData(12);
	const { data: fx, loading: fxLoading } = useExchangeRates();
	const { data: coverage, loading: coverageLoading } = useTranslationCoverage();

	// Which "Voir tout" modal is open (server-paginated full feed): the caller's own journal or the team's.
	const [changesModal, setChangesModal] = useState<null | "me" | "all">(null);

	return (
		<div className="min-h-full overflow-x-hidden bg-slate-50 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
			<div className="w-full space-y-5">
				{/* Top rates bar : key currencies against the CDF with a green/red tendency curve (no animation). */}
				<FxBar data={fx} loading={fxLoading} />

				{/* Hero : edge traffic for every public instance, the first thing an admin checks. */}
				<AnalyticsSection />

				{/* Weekly preview access code (hidden when no secret is configured, e.g. local dev)
				    + l'équipe avec ses badges de rôle, côte à côte dès la tablette. */}
				<div className="grid min-w-0 grid-cols-1 items-stretch gap-5 md:grid-cols-2">
					<PreviewAccessCard />
					<TeamCard />
				</div>

				{/* Three equal-height glances: service health, the team's tickets, your own changes. On tablet
				    (md) service status + tickets sit side by side; the third wraps below. */}
				<div className="grid min-w-0 grid-cols-1 items-stretch gap-5 md:grid-cols-2 lg:grid-cols-3">
					<ServiceStatus targets={networkData?.targets ?? []} connected={connected} />
					<TicketsPanel />
					<ChangesFeed logs={feed} loading={logsLoading} currentUser={changeAuthorName} currentUserEmail={changeAuthorEmail} scope="me" title="Mes dernières modifications" limit={6} className="md:col-span-2 lg:col-span-1" onShowAll={() => setChangesModal("me")} />
				</div>

				{/* Two long panels: the whole team's recent changes, and translation coverage : placed BEFORE
				    the latest articles so the daily-work signals sit above the editorial showcase. */}
				<div className="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-2">
					<ChangesFeed
						logs={feed}
						loading={logsLoading}
						currentUser={changeAuthorName}
						currentUserEmail={changeAuthorEmail}
						scope="all"
						title="Toutes les modifications récentes"
						subtitle="Toute l'équipe"
						limit={14}
						connected={connected}
						bodyClassName="max-h-[380px] overflow-auto"
						onShowAll={() => setChangesModal("all")}
					/>
					<TranslationCoverage data={coverage} loading={coverageLoading} />
				</div>

				{/* Four cards : the latest articles, each with its illustration + summary. */}
				<ArticleCards />
			</div>

			<RecentChangesModal
				open={changesModal !== null}
				scope={changesModal ?? "all"}
				currentUser={changeAuthorName}
				currentUserEmail={changeAuthorEmail}
				onClose={() => setChangesModal(null)}
			/>
		</div>
	);
};

export { Dashboard };
export default Dashboard;
